import clsx from 'clsx'
import groupBy from 'lodash.groupby'
import { Icon } from '@iconify/react'
import { Button } from '@pepper/ui'
import { ColumnsType } from 'antd/lib/table'

import TopicDetailsPopover from 'components/idea-lab/details-popover/TopicDetailsPopover'
import LoadingBar from 'components/LoadingBar'
import GoogleRankPill from 'components/idea-lab/GoogleRankPill'
import PotentialTrafficTooltipIcon from 'components/idea-lab/PotentialTrafficTooltipIcon'
import { kFormat } from '../../utils'
import KeywordPill from '../../Topics/KeywordPill'

import { SuggestedTopic } from 'types/topic'
import { KeywordLabel } from 'types/keyword'

import s from './TopicSuggestionsTable.module.css'

type Props = {
  isDomainRemoved: boolean
  companyId: string
  isLoadingKeywordLabel: boolean
  disableCta: boolean
  isGoogleConnected: boolean
  isContentIdeasPage: boolean
  handleAddToProject: (topic: SuggestedTopic) => void
  onEditTopic: (topic: SuggestedTopic) => void
  onGetKeywordLabel: (topicSuggestionId: string) => void
  pageRankingResults?: KeywordLabel
  domain?: string
}

export const getColumns = <RecordType extends SuggestedTopic>({
  companyId,
  isLoadingKeywordLabel,
  disableCta,
  isGoogleConnected,
  handleAddToProject,
  onEditTopic,
  onGetKeywordLabel,
  pageRankingResults,
  domain,
  isDomainRemoved,
  isContentIdeasPage,
}: Props): ColumnsType<RecordType> => {
  return [
    {
      key: 'topics',
      title: 'Topics',
      dataIndex: 'topics',
      ellipsis: true,
      className: s.cell,
      render: (_, record, index) => {
        const group = groupBy(record?.keywords, 'type')
        const primaryKeywords = group?.primary ?? []
        const secondaryKeywords = group?.secondary ?? []
        const secondaryKeywordCount = secondaryKeywords?.length ?? 0
        const visibleSecondaryKeywords = secondaryKeywords.slice(0, 2)
        const remainingSecondaryCount =
          secondaryKeywordCount - visibleSecondaryKeywords.length
        const isProcessing = ['pending', 'processing', 'error'].includes(
          record.keyword_generation_status
        )
        // Taking error as processing completed state here because
        // error just means that traffic generation is not possible
        // for the secondary keywords
        const isProcessingComplete =
          secondaryKeywordCount > 0 &&
          ['failed', 'completed'].includes(record.keyword_generation_status)

        return (
          <div className={s.wrapper} data-id={`discovery-topic-${index + 1}`}>
            <div className={s.topicTitle}>
              {primaryKeywords.length > 0 ? (
                <TopicDetailsPopover
                  isLoading={isLoadingKeywordLabel}
                  onGetKeywordLabel={() =>
                    onGetKeywordLabel(primaryKeywords?.[0]?.id)
                  }
                  keywordLabel={pageRankingResults}
                  topSearchResults={pageRankingResults?.top_ranking_details}
                  selfRankingPages={pageRankingResults?.self_ranking_pages}
                  domain={domain}
                  isGoogleConnected={isGoogleConnected}
                  companyId={companyId}
                  secondaryKeywords={secondaryKeywords}
                  topic={record}
                  primaryKeyword={primaryKeywords[0]?.keyword}
                  updatedAt={pageRankingResults?.updated_at}
                  isDomainRemoved={isDomainRemoved}
                >
                  <div className={s.nameWrapper}>
                    <div title={record.name} className={s.name}>
                      {record.name}
                    </div>
                    <Icon
                      icon="lucide:chevrons-right"
                      className={s.popoverIcon}
                      height={20}
                      width={20}
                    />
                  </div>
                </TopicDetailsPopover>
              ) : (
                <div title={record.name} className={s.name}>
                  {record.name}
                </div>
              )}
              <Button
                size="small"
                type="text"
                iconPlacement="right"
                className={s.editBtn}
                disabled={disableCta}
                icon={<Icon icon="lucide:edit-3" width={16} height={16} />}
                onClick={() => onEditTopic(record)}
              />
            </div>
            {primaryKeywords.length > 0 && (
              <div className={s.keywords}>
                {primaryKeywords?.map(keyword => (
                  <KeywordPill
                    key={keyword.id}
                    keyword={keyword.keyword}
                    volume={keyword.volume}
                    isPrimary={true}
                  />
                ))}
                {visibleSecondaryKeywords.map(keyword => (
                  <KeywordPill
                    key={keyword.id}
                    keyword={keyword.keyword}
                    volume={keyword.volume}
                  />
                ))}
                {isProcessingComplete && !!remainingSecondaryCount && (
                  <TopicDetailsPopover
                    isLoading={isLoadingKeywordLabel}
                    onGetKeywordLabel={() =>
                      onGetKeywordLabel(primaryKeywords?.[0]?.id)
                    }
                    keywordLabel={pageRankingResults}
                    topSearchResults={pageRankingResults?.top_ranking_details}
                    selfRankingPages={pageRankingResults?.self_ranking_pages}
                    domain={domain}
                    isGoogleConnected={isGoogleConnected}
                    companyId={companyId}
                    secondaryKeywords={secondaryKeywords}
                    topic={record}
                    primaryKeyword={primaryKeywords[0]?.keyword}
                    updatedAt={pageRankingResults?.updated_at}
                    isDomainRemoved={isDomainRemoved}
                  >
                    <KeywordPill keyword={`+${remainingSecondaryCount}`} />
                  </TopicDetailsPopover>
                )}
                {isProcessing && (
                  <LoadingBar width={31} height={19} borderRadius={50} />
                )}
              </div>
            )}
            <div className={s.rankWrapper}>
              {primaryKeywords?.[0]?.self_top_position && (
                <GoogleRankPill
                  googleRank={primaryKeywords[0].self_top_position}
                />
              )}
              {primaryKeywords?.[0]?.self_top_topic && (
                <div
                  className={s.rankedTopic}
                  title={primaryKeywords[0].self_top_topic}
                >
                  {primaryKeywords[0].self_top_topic}
                </div>
              )}
            </div>
          </div>
        )
      },
    },
    {
      key: 'potential_traffic',
      title: (
        <div className={clsx(s.right, s.traffic)}>
          Potential Traffic
          <PotentialTrafficTooltipIcon />
        </div>
      ),
      dataIndex: '',
      width: 130,
      className: clsx(s.cell, s.right),
      render: (_, record) => {
        const isProcessing = ['pending', 'processing', 'error'].includes(
          record.keyword_generation_status
        )
        const isProcessingComplete =
          ['failed', 'completed'].includes(record.keyword_generation_status) &&
          !!record.expected_traffic_start &&
          !!record.expected_traffic_end
        const hasLowVolume =
          isProcessingComplete && record.expected_traffic_start! < 10

        if (isProcessing) {
          return <LoadingBar width={110} height={20} borderRadius={8} />
        }

        return (
          <>
            {isProcessingComplete && !hasLowVolume ? (
              <div className={s.potentialTraffic}>
                {kFormat(record.expected_traffic_start!)}&nbsp;-&nbsp;
                {kFormat(record.expected_traffic_end!)}
                <Icon icon="uil:arrow-growth" width={18} height={18} />
              </div>
            ) : (
              <div className={s.lowVolume}>
                <Icon icon="lucide:alert-triangle" width={16} height={16} />
                Low volume
              </div>
            )}
          </>
        )
      },
    },
    {
      key: '',
      title: null,
      dataIndex: '',
      ellipsis: true,
      width: isContentIdeasPage ? 200 : 180,
      className: clsx(s.cell, s.right),
      render: (_, record, index) => {
        return (
          <div className={s.action}>
            <Button
              type="link"
              iconPlacement="left"
              disabled={disableCta}
              icon={<Icon icon="lucide:plus" width={18} height={18} />}
              onClick={() => handleAddToProject(record)}
              data-id={`discovery-topic-${index + 1}-add-project-button`}
            >
              Add to Project
            </Button>
          </div>
        )
      },
    },
  ]
}
